import { LEGAL_ENTITY } from "@/config/legal";
import { A, LegalDocument, LegalSection, LegalTable, List, P } from "./LegalLayout";

/**
 * Legal pages shown on a cleaning business's own website (subdomain or custom
 * domain). The business is the controller for its customers' information and
 * The Polished Version is its processor, so these pages speak for the business
 * and name it, with a short line that the site is provided by The Polished Version.
 */

export interface TenantLegalProps {
  businessName: string;
  phone?: string | null;
}

const PLATFORM_PRIVACY_URL = `${LEGAL_ENTITY.websiteUrl}/privacy`;
const PLATFORM_TERMS_URL = `${LEGAL_ENTITY.websiteUrl}/terms`;

function ContactLine({ businessName, phone }: TenantLegalProps) {
  return (
    <>
      <A href="/contact">contact {businessName}</A>
      {phone ? <> or call {phone}</> : null}
    </>
  );
}

function ProvidedBy({ policy, url }: { policy: string; url: string }) {
  return (
    <p className="mt-12 border-t border-border pt-6 text-sm text-muted-foreground" data-testid="legal-provided-by">
      This website and its booking system are provided by{" "}
      <A href={LEGAL_ENTITY.websiteUrl}>The Polished Version</A>. Read The Polished Version’s own <A href={url}>{policy}</A>.
    </p>
  );
}

export function TenantPrivacy({ businessName, phone }: TenantLegalProps) {
  const b = businessName;
  return (
    <LegalDocument
      slug="privacy"
      title="Privacy Policy"
      intro={`This notice explains how ${b} uses your personal information when you use this website to book a clean, ask for a quote, request a callback, send a message or leave a review.`}
      summary={
        <List
          items={[
            <>
              <strong className="font-semibold text-foreground">{b}</strong> is responsible for your information (the
              “controller”).
            </>,
            `This website and booking system are provided by The Polished Version, which stores and processes your information on ${b}’s behalf.`,
            `Your information is used to arrange and provide the services you ask for. It is not sold or used for advertising.`,
            <>
              To ask about your information or use your rights, <ContactLine businessName={b} phone={phone} />.
            </>,
          ]}
        />
      }
    >
      <LegalSection id="who-is-responsible" heading="Who is responsible for your information">
        <P>
          {b} (“we”, “us”) is the controller of the personal information you give on this website. The website is provided by
          The Polished Version, which acts as our processor: it hosts the website and stores and processes your information only
          on our instructions, under a data processing agreement.
        </P>
        <P>
          If we have given you a separate privacy notice, for example with a quote or contract, that notice applies as well.
        </P>
      </LegalSection>

      <LegalSection id="what-we-collect" heading="Information we collect">
        <List
          items={[
            "your name, email address, phone number and the address where the work will be done;",
            "details of your bookings, quotes and enquiries, including the service, dates, notes and any instructions you give;",
            "callback requests, and the messages you send us through this website or your customer account;",
            "reviews and ratings you leave about our work, with the name and location you choose to give;",
            "invoices and whether they have been paid (but not your card details — see Payments below);",
            "if we use them, checklists and photos taken at your property to record the work we did; and",
            "if you open your customer account, the sign-in link we email you and when you used it.",
          ]}
        />
      </LegalSection>

      <LegalSection id="how-we-use-it" heading="How we use it and why">
        <LegalTable
          caption="How your information is used and the lawful basis"
          head={["What we use it for", "Lawful basis"]}
          rows={[
            ["Arranging, confirming and providing your clean, and sending confirmations, reminders and invoices", "Contract, or steps you asked for before a contract"],
            ["Replying to enquiries, quote requests, callback requests and messages", "Steps you asked for before a contract, and our legitimate interests in responding to you"],
            ["Showing your review on our website, with the name and location you give, once we have approved it", "Our legitimate interests in sharing customer feedback (you can ask us to remove it)"],
            ["Keeping records of work and payments", "Legal obligation (accounting records) and our legitimate interests in dealing with any questions or claims"],
            ["Keeping this website and your information secure", "Our legitimate interests"],
          ]}
        />
      </LegalSection>

      <LegalSection id="payments" heading="Payments">
        <P>
          If you pay by card, you pay on Stripe’s secure payment page. Your card details go straight to Stripe — neither {b} nor
          The Polished Version sees your full card number. If you pay using a PayPal link, PayPal handles the payment. Stripe and
          PayPal also use your payment information under their own privacy policies.
        </P>
      </LegalSection>

      <LegalSection id="who-we-share-with" heading="Who we share it with">
        <List
          items={[
            "our team members who need it to carry out or manage your clean;",
            <>
              The Polished Version, which provides this website, and the service providers it uses to host data, send emails and
              look up addresses. Your information is stored in the EU (Ireland), and any transfer outside the UK is protected by
              approved safeguards. The full list is in <A href={PLATFORM_PRIVACY_URL}>The Polished Version’s Privacy Policy</A>;
            </>,
            "Stripe or PayPal, if you pay online; and",
            "authorities or advisers, where the law requires it.",
          ]}
        />
        <P>We don’t sell your information or share it for advertising.</P>
      </LegalSection>

      <LegalSection id="how-long" heading="How long we keep it">
        <P>
          We keep your information for as long as we need it to provide our services and keep proper records, and you can ask us
          to delete it at any time. If we erase your details, we keep only what we must for our accounts, such as invoice
          amounts, with your name and contact details removed. If we stop using The Polished Version, our data on it is
          permanently deleted 30 days after we close our account.
        </P>
      </LegalSection>

      <LegalSection id="your-rights" heading="Your rights">
        <P>
          You have the right to access your information and get a copy, have it corrected, have it deleted, restrict or object to
          how we use it, and receive it in a portable format. To make a request, <ContactLine businessName={b} phone={phone} />.
          It’s free, and we will reply within one month. We can give you a copy of your details, bookings, enquiries, invoices
          and messages, or erase them from our records.
        </P>
        <P>
          If you contact The Polished Version instead, it will pass your request to us.
        </P>
      </LegalSection>

      <LegalSection id="complaints" heading="Complaints">
        <P>
          If you’re unhappy with how we’ve handled your information, please tell us first so we can put it right. You can also
          complain to the Information Commissioner’s Office (ICO) at{" "}
          <A href="https://ico.org.uk/make-a-complaint/">ico.org.uk</A> or on 0303 123 1113.
        </P>
      </LegalSection>

      <LegalSection id="cookies" heading="Cookies">
        <P>
          This website doesn’t use analytics or advertising cookies. Our <A href="/cookies">Cookie Policy</A> explains the small
          amount of browser storage it uses.
        </P>
      </LegalSection>

      <ProvidedBy policy="Privacy Policy" url={PLATFORM_PRIVACY_URL} />
    </LegalDocument>
  );
}

export function TenantTerms({ businessName, phone }: TenantLegalProps) {
  const b = businessName;
  return (
    <LegalDocument
      slug="terms"
      title="Terms and Conditions"
      intro={`These terms cover your use of ${b}’s website, including booking requests, quotes, enquiries and messages.`}
      summary={
        <List
          items={[
            `Any booking you make is with ${b}, which provides the cleaning services.`,
            "The website is provided by The Polished Version, which isn’t part of your booking.",
            `Prices, payments and cancellations are set by ${b} and shown when you book.`,
            "Nothing here affects your legal rights as a consumer.",
          ]}
        />
      }
    >
      <LegalSection id="who-you-are-dealing-with" heading="Who you are dealing with">
        <P>
          This website belongs to {b} (“we”, “us”). Any booking, quote or service you arrange here is an agreement between you
          and {b}. The website and booking system are provided by The Polished Version, which is not a party to that agreement
          and is not responsible for the cleaning services.
        </P>
      </LegalSection>

      <LegalSection id="bookings" heading="Bookings and quotes">
        <P>
          Depending on how we have set up our booking, your booking is either confirmed straight away or sent to us as a request
          that we will confirm. Either way, we will email you to let you know. A quote is an estimate for the work described and
          becomes an agreement once you accept it and we confirm.
        </P>
        <P>
          Please give accurate details, including the address, access arrangements and anything we should know about the
          property, so we can do the job properly and safely.
        </P>
      </LegalSection>

      <LegalSection id="prices-payments" heading="Prices and payments">
        <P>
          Prices are set by {b} and shown on this website or in your quote. If the work turns out to be different from what was
          booked, we will discuss any change in price with you before going ahead. If we ask for payment online, you pay securely
          through Stripe or PayPal, and their terms also apply to the payment.
        </P>
      </LegalSection>

      <LegalSection id="cancellations" heading="Cancellations and changes">
        <P>
          Our cancellation and payment policy is shown when you book. If you need to change or cancel a booking, please let us
          know as early as possible. Any refund is handled by {b}.
        </P>
      </LegalSection>

      <LegalSection id="reviews-messages" heading="Reviews and messages">
        <P>
          Reviews and messages must be honest and must not be offensive, unlawful or include other people’s personal information.
          We may choose not to publish, or remove, reviews that break these rules.
        </P>
      </LegalSection>

      <LegalSection id="using-this-website" heading="Using this website">
        <P>
          Please use this website only for lawful purposes and don’t try to disrupt it, access other people’s information, or
          submit false bookings. We try to keep the information here accurate and up to date, but it may sometimes contain errors.
        </P>
      </LegalSection>

      <LegalSection id="liability" heading="Your rights and our liability">
        <P>
          {b} is responsible for the services it provides to you. Nothing in these terms affects your legal rights as a
          consumer, or limits liability that cannot be limited by law, such as for death or personal injury caused by negligence.
        </P>
      </LegalSection>

      <LegalSection id="questions" heading="Questions">
        <P>
          If you have a question or a problem with a booking, <ContactLine businessName={b} phone={phone} />. Our{" "}
          <A href="/privacy">Privacy Policy</A> explains how we use your information.
        </P>
      </LegalSection>

      <ProvidedBy policy="Terms and Conditions" url={PLATFORM_TERMS_URL} />
    </LegalDocument>
  );
}
